/**
 * Deck builder overlay — the pre-match screen where you assemble your deck, so the game
 * starts to feel like a collection / deck builder.
 *
 * ATTACK: you pick exactly ONE card at each distance — Long, Mid, Short (a card launches from
 * the tee matching its POWER, so this is one shot per range). DEFENSE: pick any (min 2). Cards
 * render via the shared {@link fillCardFace}. On "Play" the chosen cards seed the match deck.
 * Pure DOM — no sim mutation.
 */
import type { Club, DefenseCard, TeeBoxKey } from '../sim/types.ts';
import { boxForPower } from '../sim/constants.ts';
import { fillCardFace } from './cardFace.ts';

export interface DeckSelection {
  readonly offense: Club[];
  readonly defense: DefenseCard[];
}

export interface DeckBuilderDeps {
  /** The full roster (owned + not). Unowned cards render LOCKED — aspirational + unpickable. */
  readonly offense: readonly Club[];
  readonly defense: readonly DefenseCard[];
  /** The set of card ids the player owns, or null/undefined ⇒ treat everything as owned (offline/solo). */
  ownedIds?(): ReadonlySet<string> | null;
  /** Begin the match with the chosen deck. */
  onPlay(deck: DeckSelection): void;
  /** Return to the start screen. */
  onBack(): void;
}

export interface DeckBuilder {
  show(): void;
  hide(): void;
  /** Re-evaluate ownership (locks + valid picks) — call when the collection changes. */
  refresh(): void;
  readonly el: HTMLElement;
}

const MIN_DEFENSE = 2;
const BOX_ORDER: readonly TeeBoxKey[] = ['long', 'mid', 'short'];
const BOX_LABEL: Record<TeeBoxKey, string> = { long: 'Long', mid: 'Mid', short: 'Short' };

/** Build the deck-builder overlay under `root`. Defaults to the first card at each distance. */
export function createDeckBuilder(root: HTMLElement, deps: DeckBuilderDeps): DeckBuilder {
  // Group the attack cards by the tee their POWER launches from.
  const byBox: Record<TeeBoxKey, Club[]> = { long: [], mid: [], short: [] };
  for (const card of deps.offense) byBox[boxForPower(card.power)].push(card);

  // Exactly one attack card per distance (default: the first in each group).
  const offPick: Record<TeeBoxKey, string> = {
    long: byBox.long[0]?.id ?? '',
    mid: byBox.mid[0]?.id ?? '',
    short: byBox.short[0]?.id ?? '',
  };
  // Defense: any subset (min 2); default all (reconciled to OWNED on show).
  const pickedDef = new Set<string>(deps.defense.map((c) => c.id));

  /** Owned-check: a null/absent owned set means "all owned" (offline/solo fallback). */
  const isOwned = (id: string): boolean => {
    const set = deps.ownedIds?.();
    return set == null || set.has(id);
  };

  const el = document.createElement('section');
  el.className = 'fc-overlay fc-overlay--deck';

  const title = document.createElement('h1');
  title.className = 'fc-overlay__title fc-overlay__title--sm';
  title.textContent = 'Build Your Deck';

  const blurb = document.createElement('p');
  blurb.className = 'fc-overlay__blurb';
  blurb.textContent = 'One attack card at each distance — Long hits from the back tee, Short is a close approach. Then pick your defense.';

  const scroll = document.createElement('div');
  scroll.className = 'fc-deck__scroll';

  const refreshers: (() => void)[] = [];

  /** A card button wired to a click handler; returns the element + an is-picked refresher. */
  function makeCard(card: Club | DefenseCard, picked: () => boolean, onTap: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `fc-card fc-card--${'power' in card ? 'off' : 'def'} fc-deck__card`;
    fillCardFace(btn, card);
    btn.addEventListener('click', () => {
      if (!isOwned(card.id)) return; // locked: you don't own it yet
      onTap();
    });
    refreshers.push(() => {
      const owned = isOwned(card.id);
      btn.classList.toggle('is-picked', owned && picked());
      btn.classList.toggle('is-locked', !owned);
    });
    return btn;
  }

  // --- ATTACK: one single-select row per distance ---------------------------
  const attack = document.createElement('div');
  attack.className = 'fc-deck__section';
  const attackHead = document.createElement('div');
  attackHead.className = 'fc-deck__head';
  const attackName = document.createElement('span');
  attackName.className = 'fc-deck__head-name';
  attackName.textContent = 'Attack';
  attackHead.appendChild(attackName);
  attack.appendChild(attackHead);

  for (const box of BOX_ORDER) {
    const cards = byBox[box];
    if (cards.length === 0) continue;
    const group = document.createElement('div');
    group.className = 'fc-deck__group';
    const sub = document.createElement('span');
    sub.className = 'fc-deck__sub';
    sub.textContent = BOX_LABEL[box];
    // Each distance is a HORIZONTAL scroller (swipe through the specialty clubs for that range).
    const grid = document.createElement('div');
    grid.className = 'fc-deck__hrow';
    for (const card of cards) {
      grid.appendChild(
        makeCard(
          card,
          () => offPick[box] === card.id,
          () => {
            offPick[box] = card.id; // single-select: replaces the pick for this distance
            refreshAll();
          },
        ),
      );
    }
    group.append(sub, grid);
    attack.appendChild(group);
  }

  // --- DEFENSE: multi-select ------------------------------------------------
  const defense = document.createElement('div');
  defense.className = 'fc-deck__section';
  const defHead = document.createElement('div');
  defHead.className = 'fc-deck__head';
  const defName = document.createElement('span');
  defName.className = 'fc-deck__head-name';
  defName.textContent = 'Defense';
  const defTally = document.createElement('span');
  defTally.className = 'fc-deck__head-tally';
  defHead.append(defName, defTally);
  const defGrid = document.createElement('div');
  defGrid.className = 'fc-deck__grid';
  for (const card of deps.defense) {
    defGrid.appendChild(
      makeCard(
        card,
        () => pickedDef.has(card.id),
        () => {
          if (pickedDef.has(card.id)) pickedDef.delete(card.id);
          else pickedDef.add(card.id);
          refreshAll();
        },
      ),
    );
  }
  defense.append(defHead, defGrid);

  scroll.append(attack, defense);

  // --- Footer ---------------------------------------------------------------
  const footer = document.createElement('div');
  footer.className = 'fc-deck__footer';
  const back = document.createElement('button');
  back.type = 'button';
  back.className = 'fc-btn fc-btn--ghost';
  back.textContent = 'Back';
  back.addEventListener('click', () => deps.onBack());
  const play = document.createElement('button');
  play.type = 'button';
  play.className = 'fc-btn';
  play.textContent = 'Play';
  play.addEventListener('click', () => {
    if (play.disabled) return;
    const offense = BOX_ORDER.map((box) => byBox[box].find((c) => c.id === offPick[box])).filter(
      (c): c is Club => c !== undefined,
    );
    deps.onPlay({ offense, defense: deps.defense.filter((c) => pickedDef.has(c.id)) });
  });
  footer.append(back, play);

  el.append(title, blurb, scroll, footer);
  root.appendChild(el);

  /** Drop any pick the player doesn't own → fall back to an owned card at that distance (or none). */
  function reconcile(): void {
    for (const box of BOX_ORDER) {
      if (!isOwned(offPick[box])) {
        offPick[box] = byBox[box].find((c) => isOwned(c.id))?.id ?? '';
      }
    }
    for (const id of [...pickedDef]) if (!isOwned(id)) pickedDef.delete(id);
  }

  function refreshAll(): void {
    for (const r of refreshers) r();
    defTally.textContent = `${pickedDef.size} · min ${MIN_DEFENSE}`;
    defTally.classList.toggle('is-short', pickedDef.size < MIN_DEFENSE);
    play.disabled = pickedDef.size < MIN_DEFENSE;
  }
  refreshAll();

  return {
    el,
    show() {
      reconcile();
      refreshAll();
      el.style.display = 'flex';
    },
    hide() {
      el.style.display = 'none';
    },
    refresh() {
      reconcile();
      refreshAll();
    },
  };
}
